'use client';

import { Box, Theme } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode } from 'react';

export type StickyActionBarProps = Readonly<{
  children: ReactNode;
  'data-testid'?: string;
  /**
   * The horizontal padding the parent imposes on this bar, so the bar can negate it and reach the
   * pane edges itself, whatever that padding is. Not measured at runtime: there are two callers
   * today and both know their own padding, so each passes its own value explicitly. Default
   * `'0px'`: a caller whose parent has no horizontal padding (e.g. `MagicLinkContinue.tsx`) has
   * nothing to negate.
   */
  parentPaddingX?: string;
  /**
   * The vertical padding the parent imposes on this bar's bottom, so the bar can negate it and
   * reach the pane's own bottom edge, whatever that padding is. Mirrors `parentPaddingX` exactly:
   * same defaulting, same negation, not measured at runtime. Default `'0px'`: a caller whose
   * parent has no bottom padding to negate (e.g. `StepWrapper.tsx`'s wrapping `Stack`, which only
   * sets `px`) has nothing to negate, and the consent screen it reaches via
   * `ConsentsMobileLayout.tsx` must not move.
   */
  parentPaddingBottom?: string;
  /**
   * The bar's own internal horizontal padding around its children, independent of the negated
   * `parentPaddingX` above. Default `theme.semantic.spacing['2lg']` (24px), the board's value for
   * the magic-link screens. A caller whose content already supplies its own inset (e.g.
   * `ConsentFooterContent`'s 12px submit/back row) should pass `'0px'` here, or the two insets
   * stack and the content moves again.
   */
  contentInset?: string;
}>;

/**
 * The board's mobile-only sticky action: `position: sticky; bottom: 0`, nothing measured, nothing
 * reserved. The condition (pins when the page is longer than the viewport, rests in the flow when
 * it is not) falls entirely out of `position: sticky` on its own; this component does not branch on
 * viewport height and does not need to. Desktop is not this component's concern: `display: 'contents'`
 * at `md` and up removes this wrapper from layout entirely, and a caller renders a bare button
 * directly on desktop rather than relying on that (see the D1a task).
 *
 * This is the first use of `position: sticky` in this codebase (verified by grep against
 * origin/main and the stack tip). Deliberately not built on StepWrapper's `footerMode='sticky'`,
 * which is not actually sticky: that mode fixes itself to the viewport, measures the footer's
 * height with a resize-observing hook and reserves that height as padding with a large first-paint
 * spacing fallback. None of that machinery is needed here: a sticky-positioned element occupies
 * its own space in the document flow, so there is nothing to measure and nothing to reserve.
 */
function StickyActionBarComponent({
  children,
  'data-testid': dataTestId,
  parentPaddingX = '0px',
  parentPaddingBottom = '0px',
  contentInset,
}: StickyActionBarProps): ReactElement {
  return (
    <Box
      data-testid={dataTestId}
      sx={(theme: Theme) => {
        const inset = contentInset ?? theme.semantic.spacing['2lg'];

        return {
          display: ['flex', 'flex', 'contents'],
          position: ['sticky', 'sticky', 'static'],
          bottom: 0,
          zIndex: 2,
          flexDirection: 'column',
          // Both real callers (MagicLinkContinue.tsx, RecoverInvitation.tsx) centre their own
          // content (`alignItems: 'center'`), which leaves a flex item free to shrink to its own
          // content width unless the item itself says otherwise. This bar must always span its
          // parent's full available width regardless of what the parent's own alignment does, so
          // it declares that itself rather than depending on a caller to remember it (a caller
          // that forgot would silently get a bar, and therefore a `fullWidth` button inside it,
          // that shrinks to the button's own label). `width: 'auto'` (the default) plus
          // `alignSelf: 'stretch'` is deliberate, not `width: '100%'`: this element also carries
          // the negative horizontal margin below that cancels `parentPaddingX` to bleed to the
          // pane edges, and a `100%` width plus that same negative margin computes short of the
          // frame (the margin pulls the box wider than its own 100%-of-parent width already
          // claimed), leaving the button narrow and off-centre again. `alignSelf: 'stretch'`
          // sizes the box against the parent's content-box width the same way `align-items:
          // stretch` would if the parent allowed it, and composes correctly with the margin.
          alignSelf: 'stretch',
          gap: theme.semantic.spacing.sm,
          // This used to carry a hardcoded -24px horizontal/bottom margin, described as
          // cancelling "the stack's 24px padding-bottom" and matching "RecoverInvitation's own
          // stack padding". Verified false on both counts: RecoverInvitation has zero references
          // to this component, and this component's two real callers use two different, non-24px
          // paddings. A single hardcoded margin cannot be right for two callers with two
          // different paddings, so the bar now takes the parent's horizontal padding as an
          // explicit prop (`parentPaddingX`) and negates that, rather than inheriting or
          // measuring it. `MagicLinkContinue.tsx` passes nothing (its parent `Stack` has 0px
          // padding at `xs`, so the default `'0px'` has nothing to negate); `StepWrapper.tsx`
          // (via `ConsentsMobileLayout`'s footer slot) passes `tokens.spacingBase` (16px), its
          // parent `Stack`'s own `px: [tokens.spacingBase, 0]`. `6px` (top) is the one literal
          // board measurement that survives unrelated to either padding: a fixed gap from
          // whatever content precedes the bar.
          //
          // The bottom term mirrors the horizontal one via `parentPaddingBottom`, added later:
          // the design board's own equivalent element carries `margin: 6px -24px -24px` (negative
          // on the bottom too), which this component never implemented until now. Without it, the
          // bar's own 28px bottom padding stacked on top of whatever bottom padding the parent
          // column already had, rather than being pulled down through it.
          // `RecoverInvitation.tsx` and `MagicLinkContinue.tsx` both pass `tokens.spacing2lg`
          // (24px), each parent's own established padding value; `StepWrapper.tsx`'s consent
          // caller passes nothing (its wrapping `Stack` sets `px` only, no bottom padding to
          // negate), so the default `'0px'` leaves that screen exactly where it was.
          margin: [
            `6px calc(${parentPaddingX} * -1) calc(${parentPaddingBottom} * -1)`,
            `6px calc(${parentPaddingX} * -1) calc(${parentPaddingBottom} * -1)`,
            0,
          ],
          // The bar's own internal breathing room around its children, unrelated to the negated
          // margin above: `28px` (bottom, a literal board measurement with no matching spacing
          // token) is fixed; the horizontal value is `contentInset`, an explicit prop rather than
          // a hardcoded token, because it is not the same value for both callers. Default
          // `theme.semantic.spacing['2lg']` (24px) is the board's value for the magic-link
          // screens and matches the literal `padding: '28px 24px'` already used for this geometry
          // in the Task 3 story. `StepWrapper.tsx`'s consent caller passes `'0px'`:
          // `ConsentFooterContent` already supplies its own 12px inset on its submit/back row, so
          // adding this bar's own 24px on top would double it and move the screen again.
          padding: [
            `${theme.semantic.spacing.base} ${inset} 28px`,
            `${theme.semantic.spacing.base} ${inset} 28px`,
            0,
          ],
          background: [
            `linear-gradient(to top, ${theme.palette.background.default} 0 72%, transparent)`,
            `linear-gradient(to top, ${theme.palette.background.default} 0 72%, transparent)`,
            'none',
          ],
        };
      }}
    >
      {children}
    </Box>
  );
}

export const StickyActionBar = withClarityTheme(StickyActionBarComponent);
